/**
 * IntelligenceStudioPage (Intelligence Studio)
 *
 * EXACT copy of the main page controller previously in IntelligenceStudioPage.jsx.
 * Moved to /containers to keep the page file SRP-clean.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import intelligenceStudioApi from '../../../api/intelligenceStudioApi';
import { useNanoMetadataUpload } from '../../../hooks/useNanoMetadataUpload';
import {
  useCitationNavigation,
  useNanoMetadataTableSync,
  useNotebookCreation,
  useNotebookFileActions,
  useNotebookRouting,
  useNotebooksCrud,
  useDeadPanelDetector,
  useNotebookPolling,
  useNotebookUrlLoader,
  useResizablePanels,
} from '@/pages/intelligence-studio/hooks';
import EmptyStateView from '@/pages/intelligence-studio/views/EmptyStateView';

// ✅ Extracted Views (presentational-only)
import HomeView from '@/pages/intelligence-studio/views/HomeView';
import NotebookView from '@/pages/intelligence-studio/views/NotebookView';
import TraceInspector from '@/components/TraceInspector/TraceInspector';
import UltraDebugPanel from '@/components/UltraDebug/UltraDebugPanel';

// ==========================================
// MAIN PAGE COMPONENT
// ==========================================

const LandingLoadingView = () => (
  <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-amber-50/30 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-900">
    <div className="flex flex-col items-center gap-3 text-slate-600 dark:text-neutral-400">
      <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      <span className="text-sm font-medium">Loading Intelligence Studio...</span>
    </div>
  </div>
);

const IntelligenceStudioPage = () => {
  // URL parameters for persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const notebookIdFromUrl = searchParams.get('id');

  // Notebooks state
  const [notebooks, setNotebooks] = useState([]);
  const [selectedNotebook, setSelectedNotebook] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);

  // View state: 'home' (grid of all notebooks) or 'notebook' (individual notebook view)
  const [view, setView] = useState(notebookIdFromUrl ? 'notebook' : 'home');

  // Resizable panels container ref
  const containerRef = useRef(null);

  // Resizable panel system with collapse support
  const {
    leftWidth,
    rightWidth,
    leftCollapsed,
    rightCollapsed,
    handleLeftResize,
    handleRightResize,
    toggleLeftCollapse,
    toggleRightCollapse,
    expandLeft,
    expandRight,
  } = useResizablePanels(containerRef, { left: 280, right: 320 });

  // Selected file for preview (right panel)
  const [selectedFile, setSelectedFile] = useState(null);

  // Citation navigation (for jumping to source)
  const [targetPage, setTargetPage] = useState(null);
  const [targetText, setTargetText] = useState(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    notebookId: null,
    notebookName: '',
  });

  // Search state for home view
  const [searchQuery, setSearchQuery] = useState('');

  // ✅ ENTERPRISE: Quota / usage status (for utilization indicator + user-friendly guidance)
  const [quotaStatus, setQuotaStatus] = useState(null);

  // ✅ ENTERPRISE: Upload lock to prevent double-uploads
  const uploadLockRef = useRef(new Set()); // Track files currently being uploaded

  // ✅ NANO-METADATA ENGINE: Instant file DNA extraction
  const {
    rows: nanoMetadataRows,
    isUploading: isNanoAnalyzing,
    uploadFile: uploadFileForNanoMetadata,
    uploadFiles: uploadFilesForNanoMetadata,
    uploadFilesBatch: uploadFilesBatchForNanoMetadata, // ✅ NEW: Batch upload for large file sets
    clearRows: clearNanoMetadataRows,
    setRowsDirectly: setNanoMetadataRowsDirectly, // ✅ NEW: Set rows directly
    addRows: addNanoMetadataRows, // ✅ NEW: Add rows
    removeRow: removeNanoMetadataRow,
    retryFile: retryNanoMetadataFile, // ✅ Retry failed uploads
  } = useNanoMetadataUpload({ apiNamespace: 'intelligence-studio', serverEnhancement: false });

  // ✅ SRP: Nano-Metadata SmartTable conversion + sync extracted into a dedicated hook
  const { convertFilesToSmartTableRows, formatBytes } = useNanoMetadataTableSync({
    selectedNotebook,
    nanoMetadataRows,
    setNanoMetadataRowsDirectly,
  });

  const { handleCreateNotebook } = useNotebookCreation({
    intelligenceStudioApi,
    uploadLockRef,
    formatBytes,
    clearNanoMetadataRows,
    setNanoMetadataRowsDirectly,
    uploadFilesBatchForNanoMetadata,
    uploadFileForNanoMetadata,
    setNotebooks,
    setSelectedNotebook,
    setShowCreateModal,
    setView,
    setSearchParams,
    setError,
  });

  // ✅ SRP: URL loader + polling extracted into dedicated hooks
  useNotebookUrlLoader({
    notebookIdFromUrl,
    selectedNotebook,
    setSelectedNotebook,
    setView,
    setNotebooks,
    setSearchParams,
    intelligenceStudioApi,
    convertFilesToSmartTableRows,
    setNanoMetadataRowsDirectly,
  });

  useNotebookPolling({
    selectedNotebook,
    setSelectedNotebook,
    setNotebooks,
    intelligenceStudioApi,
  });

  // ✅ PHASE 5: Dead-panel detector (prevents left Sources panel from freezing)
  useDeadPanelDetector({
    selectedNotebook,
    setSelectedNotebook,
    onDegraded: (e) => {
      // Non-blocking warning; keeps UX honest without crashing.
      setError(e?.message || 'Live updates stalled. Switching to polling.');
    },
  });

  const {
    refreshNotebook,
    handleUploadFile,
    handleFileSelect,
    handleRemoveDuplicates,
    handleRetryFile,
    handleRemoveFile,
    handleDeleteFile,
    handleRenameFile,
    handleReuploadFile,
    handleReprocessFile,
  } = useNotebookFileActions({
    intelligenceStudioApi,
    uploadLockRef,
    selectedNotebook,
    setSelectedNotebook,
    setNotebooks,
    setError,
    uploadFileForNanoMetadata,
    removeNanoMetadataRow,
    rightCollapsed,
    expandRight,
    selectedFile,
    setSelectedFile,
  });

  const refreshNotebookRef = useRef(refreshNotebook);
  useEffect(() => {
    refreshNotebookRef.current = refreshNotebook;
  }, [refreshNotebook]);

  // ✅ ENTERPRISE: Periodic truth-sync refresh (30s) + on window focus
  useEffect(() => {
    if (view !== 'notebook' || !selectedNotebook?.id) return;

    const refreshIfVisible = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      refreshNotebookRef.current?.();
    };

    refreshIfVisible();
    const intervalMs = 30_000;
    const intervalId = setInterval(refreshIfVisible, intervalMs);
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [view, selectedNotebook?.id]);

  const {
    fetchNotebooks,
    closeDeleteConfirmation,
    confirmDelete,
    handleDeleteNotebook,
    handleRenameNotebook,
  } = useNotebooksCrud({
    intelligenceStudioApi,
    notebooks,
    selectedNotebook,
    deleteConfirmation,
    setIsLoading,
    setNotebooks,
    setSelectedNotebook,
    setView,
    setSearchParams,
    setError,
    setHasLoadedOnce,
    setDeleteConfirmation,
  });

  const { handleSelectNotebook, handleBackToHome, handleNotebookUpdate } = useNotebookRouting({
    selectedNotebook,
    setSelectedNotebook,
    setView,
    setSearchParams,
    setNotebooks,
  });

  const { handleCitationClick } = useCitationNavigation({
    selectedNotebook,
    setSelectedFile,
    setTargetPage,
    setTargetText,
    rightCollapsed,
    expandRight,
  });

  // ✅ DEBUG: Log localStorage state on component mount
  useEffect(() => {
    console.log('🔧 [DEBUG] IntelligenceStudio mounted');
    console.log('🔧 [DEBUG] URL notebook ID:', notebookIdFromUrl);

    // Check all localStorage keys related to failed files
    const failedFileKeys = Object.keys(localStorage).filter(k => k.startsWith('failed_files_'));
    console.log(
      `🔧 [DEBUG] Found ${failedFileKeys.length} failed_files keys in localStorage:`,
      failedFileKeys
    );

    failedFileKeys.forEach(key => {
      try {
        const files = JSON.parse(localStorage.getItem(key) || '[]');
        console.log(
          `   ${key}: ${files.length} files`,
          files.map(f => f.originalName || f.name)
        );
      } catch (e) {
        console.log(`   ${key}: error parsing`);
      }
    });
  }, [notebookIdFromUrl]);

  // Fetch notebooks on mount
  useEffect(() => {
    fetchNotebooks();
  }, []);

  // ✅ ENTERPRISE: Poll quota status (non-blocking, lightweight)
  const refreshQuotaStatus = useCallback(async () => {
    try {
      const res = await intelligenceStudioApi.getQuotaStatus();
      if (res?.success && res?.data) {
        setQuotaStatus(res.data);
      }
    } catch (e) {
      // Non-critical: ignore
    }
  }, []);

  useEffect(() => {
    refreshQuotaStatus();
    const t = setInterval(refreshQuotaStatus, 30_000);
    return () => clearInterval(t);
  }, [refreshQuotaStatus]);

  // Handle timeline building
  const handleBuildTimeline = async notebookId => {
    if (!notebookId) return;
    try {
      await intelligenceStudioApi.buildTimelineFromDocs(notebookId);
      // Timeline will be refreshed via the NotesPanel
    } catch (err) {
      setError('Failed to build timeline');
    }
  };

  // Handle clause mapping
  const handleMapClauses = async (notebookId, fileIds) => {
    if (!notebookId || !fileIds?.length) return;
    try {
      const response = await intelligenceStudioApi.mapClausesAcrossFiles(notebookId, fileIds);
      console.log('Clause mapping result:', response);
      // Show result in UI (could be a modal or panel)
    } catch (err) {
      setError('Failed to map clauses');
    }
  };

  // Empty state (no notebooks at all)
  // ✅ MODULAR: Uses extracted EmptyStateView component
  if (!hasLoadedOnce && !notebookIdFromUrl) {
    return <LandingLoadingView />;
  }
  if (!isLoading && notebooks.length === 0) {
    return <EmptyStateView onCreateNotebook={handleCreateNotebook} />;
  }

  // Filter notebooks for search
  const filteredNotebooks = notebooks.filter(n =>
    n.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // HOME VIEW: Grid of all notebooks (like NotebookLM)
  // ✅ FIX: Use h-full (Outlet-relative) instead of h-screen (viewport)
  if (view === 'home') {
    return (
      <>
        <HomeView
          notebooks={notebooks}
          filteredNotebooks={filteredNotebooks}
          isLoading={isLoading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showCreateModal={showCreateModal}
          setShowCreateModal={setShowCreateModal}
          handleCreateNotebook={handleCreateNotebook}
          deleteConfirmation={deleteConfirmation}
          confirmDelete={confirmDelete}
          closeDeleteConfirmation={closeDeleteConfirmation}
          error={error}
          setError={setError}
          handleSelectNotebook={handleSelectNotebook}
          handleDeleteNotebook={handleDeleteNotebook}
          handleRenameNotebook={handleRenameNotebook}
        />
        <TraceInspector />
        <UltraDebugPanel />
      </>
    );
  }

  // NOTEBOOK VIEW: 3-column layout for individual notebook
  // ✅ FIX: Use h-full (Outlet-relative) instead of h-screen (viewport)
  return (
    <>
      <NotebookView
        notebooks={notebooks}
        selectedNotebook={selectedNotebook}
        quotaStatus={quotaStatus}
        isLoading={isLoading}
        error={error}
        setError={setError}
        containerRef={containerRef}
        leftWidth={leftWidth}
        rightWidth={rightWidth}
        leftCollapsed={leftCollapsed}
        rightCollapsed={rightCollapsed}
        handleLeftResize={handleLeftResize}
        handleRightResize={handleRightResize}
        toggleLeftCollapse={toggleLeftCollapse}
        toggleRightCollapse={toggleRightCollapse}
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        handleCreateNotebook={handleCreateNotebook}
        deleteConfirmation={deleteConfirmation}
        confirmDelete={confirmDelete}
        closeDeleteConfirmation={closeDeleteConfirmation}
        handleBackToHome={handleBackToHome}
        refreshNotebook={refreshNotebook}
        handleRenameNotebook={handleRenameNotebook}
        handleDeleteNotebook={handleDeleteNotebook}
        handleSelectNotebook={handleSelectNotebook}
        handleFileSelect={handleFileSelect}
        handleUploadFile={handleUploadFile}
        handleRemoveDuplicates={handleRemoveDuplicates}
        handleRetryFile={handleRetryFile}
        handleRemoveFile={handleRemoveFile}
        handleDeleteFile={handleDeleteFile}
        handleRenameFile={handleRenameFile}
        handleReuploadFile={handleReuploadFile}
        handleReprocessFile={handleReprocessFile}
        handleCitationClick={handleCitationClick}
        handleBuildTimeline={handleBuildTimeline}
        handleMapClauses={handleMapClauses}
        selectedFile={selectedFile}
        clearSelectedFile={() => setSelectedFile(null)}
        targetPage={targetPage}
        targetText={targetText}
        nanoMetadataRows={nanoMetadataRows}
        isNanoAnalyzing={isNanoAnalyzing}
        removeNanoMetadataRow={removeNanoMetadataRow}
        retryNanoMetadataFile={retryNanoMetadataFile}
        clearNanoMetadataRows={clearNanoMetadataRows}
        onUpdateNotebook={handleNotebookUpdate}
      />
      <TraceInspector />
      <UltraDebugPanel />
    </>
  );
};

// ✅ MOVED: NotebookCard and getNotebookIcon imported from ./components/NotebookCard

// NotebookCard is now imported from ./components/NotebookCard

// UnifiedHeader is now imported from ./components/UnifiedHeader

// ✅ EXTRACTED: SourcesPanel, FileTypeIcon, SourceFileItem, NotebookListItem
// Now imported from ./components/panels/SourcesPanel

// ✅ EXTRACTED: MainPanel -> ./components/panels/MainPanel
// ✅ EXTRACTED: NotesPanel -> ./components/panels/NotesPanel

// ✅ EXTRACTED: AGI + SRL-U view components -> ./views/agi/* and ./views/srlu/*

// ✅ MOVED TO MODULAR FILES:
// - EmptyState → ./views/EmptyStateView.jsx
// - getFileIcon, formatFileSize → ./utils/fileUtils.js
// - CreateNotebookModal, DeleteConfirmationModal, Toast → ./components/modals/

// ✅ All modals (CreateNotebookModal, DeleteConfirmationModal, Toast)
// imported from ./components/modals/ at top of file

export default IntelligenceStudioPage;
